import { useQuery } from '@tanstack/react-query';

import { Badge, Card, EmptyState, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { formatCents, formatDate } from '../../lib/formatters';

interface PaymentRow {
  id: string;
  amount: number;
  currency: string;
  type: string;
  status: string;
  description: string | null;
  createdAt: string;
  member?: { firstName: string; lastName: string };
}

export function PaymentsPage() {
  const { data, isLoading } = useQuery<{ items: PaymentRow[]; total: number }>({
    queryKey: ['payments'],
    queryFn: async () => (await api.get('/admin/payments', { params: { pageSize: 100 } })).data,
  });

  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl">Payments</h1>

      {isLoading ? (
        <Spinner />
      ) : (data?.items ?? []).length === 0 ? (
        <EmptyState title="No payments yet" description="Manual and Stripe payments will appear here." />
      ) : (
        <Card>
          <table className="w-full">
            <thead className="bg-cream-200">
              <tr>
                <th className="text-left px-4 py-2 text-sm font-medium">Date</th>
                <th className="text-left px-4 py-2 text-sm font-medium">Member</th>
                <th className="text-left px-4 py-2 text-sm font-medium">Type</th>
                <th className="text-right px-4 py-2 text-sm font-medium">Amount</th>
                <th className="text-left px-4 py-2 text-sm font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data!.items.map((p) => (
                <tr key={p.id} className="border-t border-cream-300">
                  <td className="px-4 py-2 text-sm">{formatDate(p.createdAt)}</td>
                  <td className="px-4 py-2 text-sm">
                    {p.member ? `${p.member.firstName} ${p.member.lastName}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-sm">{p.type}</td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums">
                    {formatCents(p.amount, p.currency)}
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant={p.status === 'PAID' ? 'success' : 'default'}>{p.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* TODO(section-4): filters, CSV export button → GET /admin/payments/export, manual payment modal, refund modal */}
    </div>
  );
}
